import type { IssueTypes } from '@risksmart-app/trpc/src/services/service.types';
import type { InsertChildIssueMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertChildIssueDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import type { InsertIssueInput } from '@/hooks/mutations/nativeMutationInputs';
import { createMutationHook } from '@/utils/hook-factories';

type InsertIssueTRPCInput = Omit<InsertIssueInput, 'Type'> & {
  Type: IssueTypes;
};

export const useInsertChildIssue = createMutationHook({
  name: 'insertChildIssue',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.issue.insert.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  mapInput: (variables: InsertIssueInput): InsertIssueTRPCInput => ({
    ...variables,
    Type: variables.Type as IssueTypes,
  }),
  invalidate: (trpc) => [
    trpc.frontend.issue.register.queryKey(),
    trpc.frontend.issue.issueById.queryKey(),
    trpc.frontend.issue.issuesByParentId.queryKey(),
    // COI-889: the left-nav badge reads this count family.
    trpc.frontend.issue.issueCount.queryKey(),
    // COI-471 parity: Apollo twin also evicts the parent surfaces an issue
    // can be raised against: `assessment`, `internal_audit_entity`,
    // `internal_audit_report` and `oversight_assessment`.
    trpc.frontend.assessment.pathKey(),
    trpc.frontend.internalAuditEntity.pathKey(),
    trpc.frontend.internalAuditReport.pathKey(),
    trpc.frontend.oversight.pathKey(),
    // COI-889: the my-items ribbon counts this entity.
    trpc.frontend.myItems.pathKey(),
  ],
  mapResponse: (result: {
    Id: string;
    SequentialId: number | null;
  }): InsertChildIssueMutation => ({
    insertChildIssue: {
      __typename: 'InsertChildIssueOutput',
      Id: result.Id,
      SequentialId: result.SequentialId ?? 0,
    },
  }),
  graphqlDocument: InsertChildIssueDocument,
  graphqlVariables: (variables: InsertIssueInput) => ({ object: variables }),
  evictFields: [
    'issue',
    'issue_aggregate',
    'internal_audit_entity',
    'internal_audit_report',
    'oversight_assessment',
    'assessment_by_pk',
  ],
  refetchQueries: [
    namedOperations.Query.getIssueById,
    namedOperations.Query.getIssues,
  ],
});
